import type {
  TeleopCameraDto,
  TeleopCommandRequestDto,
  TeleopSessionEventDto,
  TeleopSoundDto,
  TeleopTelemetryExportDto,
} from 'api/asyncapi'
import { useEffect, useRef, useState } from 'react'
import EventEmitter from 'utils/EventEmitter'
import { TELEOP_ACTIONS, TELEOP_MESSAGE_TYPES } from 'utils/constants'

const WEBSOCKET_PATH = '/api/websocket/teleop'

type TeleopCommandMessage = Omit<TeleopCommandRequestDto, 'meta' | 'auxiliary'> & {
  meta: TeleopCommandRequestDto['meta'] & { session_id: string }
  auxiliary: {
    sound: TeleopSoundDto
    camera: TeleopCameraDto
  }
}

function useManualMode(robotId: number | undefined, userId: number | undefined) {
  const socket = useRef<WebSocket | null>(null)
  const eventEmitter = useRef(new EventEmitter())
  const websocketMessage = useRef(0)

  const [motionData, setMotionData] = useState({
    current_angular_speed: 0,
    current_linear_speed: 0,
    target_angular_speed: 0,
    target_linear_speed: 0,
  })
  const [ping, setPing] = useState(0)

  const [webrtcUrl, setWebrtcUrl] = useState('bro30_sub')

  const pingBuffer = useRef(0)
  const requestIdCounter = useRef(0)

  const dataMessage: TeleopCommandMessage = {
    robot_id: `${robotId}`,
    meta: {
      session_id: 'session_unique_id',
      seq_num: 1,
      echo_uptime_ms: 0,
    },
    control: {
      input_mode: 'keyboard',
      emergency: false,
      speed_limit: 1.0,
      motion: {
        linear_speed: 0.0,
        angular_speed: 0.0,
      },
    },
    auxiliary: {
      sound: {
        type: 'off',
        duration_ms: 0,
      },
      camera: {
        source: 'front',
        quality: 'high',
      },
    },
  }

  const setSessionId = (sessionId: string) => {
    dataMessage.meta.session_id = sessionId
    sendMessage()
  }

  const setSequenceNumber = (sequenceNumber: number) => {
    dataMessage.meta.seq_num = sequenceNumber
    sendMessage()
  }

  const setUptime = (uptime: number) => {
    dataMessage.meta.echo_uptime_ms = uptime
    sendMessage()
  }

  const setInputMode = (inputMode: string) => {
    dataMessage.control.input_mode = inputMode
    sendMessage()
  }

  const setSpeedLimit = (speedLimit: number) => {
    dataMessage.control.speed_limit = speedLimit
    sendMessage()
  }

  const setLinearMotion = (linear: number) => {
    dataMessage.control.motion.linear_speed = linear
    sendMessage()
  }

  const setAngularMotion = (angular: number) => {
    dataMessage.control.motion.angular_speed = angular
    sendMessage()
  }

  const setSound = (sound: string) => {
    dataMessage.auxiliary.sound.type = sound
    dataMessage.auxiliary.sound.duration_ms = 800
    sendMessage()

    dataMessage.auxiliary.sound.type = 'off'
    dataMessage.auxiliary.sound.duration_ms = 0
    sendMessage()
  }

  const setCamera = (source: string) => {
    dataMessage.auxiliary.camera.source = source
    sendMessage()
  }

  const getNextRequestId = () => {
    requestIdCounter.current += 1
    return requestIdCounter.current
  }

  const sendMessage = () => {
    if (!socket.current) return
    if (socket.current.readyState !== WebSocket.OPEN) return

    const wsMessage = {
      action: TELEOP_ACTIONS.COMMAND,
      request_id: getNextRequestId(),
      data: dataMessage,
    }

    socket.current.send(JSON.stringify(wsMessage))
    dataMessage.meta.seq_num = Number(dataMessage.meta.seq_num) + 1
  }

  const _teleopInit = (ws: WebSocket) => {
    if (!robotId) return
    if (ws.readyState !== WebSocket.OPEN) return

    const data = {
      action: TELEOP_ACTIONS.INIT,
      request_id: getNextRequestId(),
      data: {
        robot_id: `${robotId}`,
        user_id: userId,
        previous_mcs_mode: 0,
      },
    }

    ws.send(JSON.stringify(data))
  }

  const _teleopExit = (ws: WebSocket) => {
    if (!robotId) return
    if (ws.readyState !== WebSocket.OPEN) return

    const data = {
      action: TELEOP_ACTIONS.EXIT,
      request_id: getNextRequestId(),
      data: {
        robot_id: `${robotId}`,
        session_id: dataMessage.meta.session_id,
      },
    }

    ws.send(JSON.stringify(data))
  }

  const getTelemetry = (message: TeleopTelemetryExportDto) => {
    const msgData = message.data

    websocketMessage.current += 1
    dataMessage.meta.session_id = msgData.meta.session_id
    dataMessage.meta.echo_uptime_ms = msgData.meta.robot_uptime_ms

    setMotionData({
      current_angular_speed: msgData.motion?.current_angular_speed ?? 0,
      current_linear_speed: msgData.motion?.current_linear_speed ?? 0,
      target_angular_speed: msgData.motion?.target_angular_speed ?? 0,
      target_linear_speed: msgData.motion?.target_linear_speed ?? 0,
    })

    pingBuffer.current += parseInt(String(msgData.network.communication_delay_ms))

    if (websocketMessage.current % 3 == 0) {
      setPing(Math.floor(pingBuffer.current / 3))
      pingBuffer.current = 0
    }
  }

  const getSessionId = (message: TeleopSessionEventDto) => {
    const msgData = message.data
    dataMessage.meta.session_id = msgData.session_id
    // eslint-disable-next-line no-console
    console.log('Get session id from server: ', msgData.session_id)
  }

  useEffect(() => {
    if (!robotId) return

    const scheme = 'ws' + (window.location.protocol === 'https:' ? 's' : '')
    const url = `${scheme}://${window.location.host}${WEBSOCKET_PATH}/${robotId}`

    const ws = new WebSocket(url)
    socket.current = ws

    const closeHandler = () => {
      // eslint-disable-next-line no-console
      console.log('Connection close ', WEBSOCKET_PATH)
    }

    const openHandler = () => {
      // eslint-disable-next-line no-console
      console.log('Connection open ', WEBSOCKET_PATH)
      _teleopInit(ws)
    }

    const errorHandler = (event: Event) => {
      // eslint-disable-next-line no-console
      console.log(event)
    }

    const messageHandler = (event: MessageEvent) => {
      try {
        if (!eventEmitter.current) return

        const data = JSON.parse(event.data)

        const eventType = data.type ?? data.topic
        if (!eventType) return

        eventEmitter.current.emit(eventType, data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }

    ws.addEventListener('open', openHandler)
    ws.addEventListener('close', closeHandler)
    ws.addEventListener('error', errorHandler)
    ws.addEventListener('message', messageHandler)

    eventEmitter.current.addListener(TELEOP_MESSAGE_TYPES.TELEMETRY, getTelemetry)
    eventEmitter.current.addListener(TELEOP_MESSAGE_TYPES.SESSION, getSessionId)

    return () => {
      try {
        _teleopExit(ws)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }

      ws.close()

      ws.removeEventListener('open', openHandler)
      ws.removeEventListener('close', closeHandler)
      ws.removeEventListener('error', errorHandler)
      ws.removeEventListener('message', messageHandler)

      eventEmitter.current.removeAllListeners()

      socket.current = null
    }
  }, [robotId])

  return {
    setSessionId,
    setSequenceNumber,
    setUptime,
    setInputMode,
    setSpeedLimit,
    setLinearMotion,
    setAngularMotion,
    setSound,
    setCamera,
    motionData,
    ping,
    webrtcUrl,
  }
}

export default useManualMode
